import { useNavigate } from "react-router-dom";
import CandidateCard from "@common/CandidateCard";

const FIELD_NAMES = {
  user_profile_role:"Job Role", profile_bio:"Bio", city_name:"City",
  country_name:"Country", state_name:"State", skill_name:"Skills",
  degrees:"Degrees", subjects:"Subjects", schools:"Schools",
  employer_names:"Employers", job_titles:"Job Titles", languages:"Languages",
};

function Pagination({ page = 1, totalPages = 1, total = 0, onChange }) {
  if (!totalPages || totalPages <= 1) return null;
  const PER_PAGE = 20;
  const from = (page - 1) * PER_PAGE + 1;
  const to   = Math.min(page * PER_PAGE, total);

  const getPages = () => {
    const pages = [];
    if (totalPages <= 7) { for (let i = 1; i <= totalPages; i++) pages.push(i); return pages; }
    pages.push(1);
    if (page - 1 > 2) pages.push("...");
    for (let i = Math.max(2, page - 1); i <= Math.min(totalPages - 1, page + 1); i++) pages.push(i);
    if (page + 1 < totalPages - 1) pages.push("...");
    pages.push(totalPages);
    return pages;
  };

  return (
    <div className="fc-pagination">
      <span className="fc-pagination-info">
        Showing <strong>{from}–{to}</strong> of <strong>{total}</strong>
      </span>
      <div className="d-flex gap-1">
        <button className="fc-pagination-btn" onClick={() => onChange && onChange(Math.max(1, page - 1))} disabled={page === 1}>
          <i className="fa-solid fa-chevron-left" style={{ fontSize: 10 }} />
        </button>
        {getPages().map((p, i) =>
          p === "..." ? (
            <span key={i} className="fc-pagination-dots">···</span>
          ) : (
            <button key={p} onClick={() => onChange && onChange(p)} className={`fc-pagination-btn${p === page ? " active" : ""}`}>{p}</button>
          )
        )}
        <button className="fc-pagination-btn" onClick={() => onChange && onChange(Math.min(totalPages, page + 1))} disabled={page === totalPages}>
          <i className="fa-solid fa-chevron-right" style={{ fontSize: 10 }} />
        </button>
      </div>
    </div>
  );
}

function MatchBadges({ item }) {
  if (!item || typeof item !== "object") return null;
  const score     = Number(item.match_score) || 0;
  const fields    = Array.isArray(item.matched_fields)   ? item.matched_fields   : [];
  const matched   = Array.isArray(item.matched_skills)   ? item.matched_skills   : [];
  const unmatched = Array.isArray(item.unmatched_skills) ? item.unmatched_skills : [];
  if (score === 0 && fields.length === 0 && matched.length === 0) return null;
  return (
    <div style={{ padding:"0 16px 12px" }}>
    <div style={{ display:"flex", flexWrap:"wrap", alignItems:"center", gap:6, paddingTop:8, borderTop:"1px solid var(--fc-border-light)" }}>
      {score > 0 && (
        <span style={{ fontSize:11, fontWeight:700, color:"#2563EB", background:"#EFF6FF", padding:"2px 9px", borderRadius:99, border:"1px solid #BFDBFE" }}>
          Score: {score}%
        </span>
      )}
      {fields.map((f, i) => (
        <span key={i} style={{ fontSize:11, fontWeight:600, color:"#D97706", background:"#FEF3C7", padding:"2px 8px", borderRadius:99 }}>
          {FIELD_NAMES[f] || f}
        </span>
      ))}
      {matched.slice(0, 3).map((s, i) => (
        <span key={i} style={{ fontSize:11, fontWeight:600, color:"#15803D", background:"#F0FDF4", padding:"2px 8px", borderRadius:99 }}>{s}</span>
      ))}
      {unmatched.slice(0, 2).map((s, i) => (
        <span key={i} style={{ fontSize:11, fontWeight:600, color:"#DC2626", background:"#FEF2F2", padding:"2px 8px", borderRadius:99 }}>{s}</span>
      ))}
    </div>
    </div>
  );
}

export default function SearchResultsPanel({
  results,
  rawResults,
  page = 1,
  totalPages = 1,
  totalResults = 0,
  onPageChange,
  ownFirm,
}) {
  const navigate = useNavigate();
  const safeList = Array.isArray(results)    ? results    : [];
  const safeRaw  = Array.isArray(rawResults) ? rawResults : [];

  if (safeList.length === 0) {
    return (
      <div style={{ background:"var(--fc-card-bg)", border:"1px solid var(--fc-card-border)", borderRadius:16, padding:"52px 24px", textAlign:"center" }}>
        {/* Illustration */}
        <div style={{ width:72, height:72, borderRadius:99, background:"var(--fc-border-light)", display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 20px" }}>
          <i className="fa-solid fa-user-magnifying-glass" style={{ fontSize:28, color:"var(--fc-text-hint)" }} />
        </div>

        <div style={{ fontSize:16, fontWeight:700, color:"var(--fc-text-primary)", marginBottom:6 }}>No candidates found</div>
        <div style={{ fontSize:13, color:"var(--fc-text-subtle)", marginBottom:24, lineHeight:1.6 }}>
          Try adjusting your search terms or filters<br/>to find the right candidates.
        </div>

        {/* Suggestions */}
        <div style={{ display:"flex", flexDirection:"column", gap:8, maxWidth:300, margin:"0 auto", textAlign:"left" }}>
          {[
            { icon:"fa-sliders",        text:"Adjust or remove active filters" },
            { icon:"fa-spell-check",    text:"Check for typos in your keywords" },
            { icon:"fa-expand",         text:"Try broader or fewer search terms" },
          ].map((s, i) => (
            <div key={i} style={{ display:"flex", alignItems:"center", gap:10, padding:"8px 12px", background:"var(--fc-bg-light)", borderRadius:9, border:"1px solid var(--fc-border-light)" }}>
              <div style={{ width:28, height:28, borderRadius:7, background:"#EFF6FF", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                <i className={`fa-solid ${s.icon}`} style={{ fontSize:11, color:"#2563EB" }} />
              </div>
              <span style={{ fontSize:12, color:"var(--fc-text-secondary)", fontWeight:500 }}>{s.text}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex flex-column gap-2">
        {safeList.map((r, i) => (
          <div key={r.id || i} style={{ background:"var(--fc-card-bg)", border:"1px solid var(--fc-card-border)", borderRadius:14, overflow:"hidden" }}>
            <CandidateCard candidate={r} onView={() => navigate(`/resources/details/${r.id}`)} />
            {safeRaw[i] && <MatchBadges item={safeRaw[i]} />}
          </div>
        ))}
      </div>
      <Pagination page={page} totalPages={totalPages} total={totalResults} onChange={onPageChange} />
    </div>
  );
}